// Scope-aware helper for persistent layout preference (Library vs Gallery)
// Precedence: explicit URL param `layout`, then localStorage (by scope), else default GRID.

import { GALLERY_ONLY, GRID, INLINE, LAYOUT, LOCAL_STORAGE_KEYS, TWOUP, VIEW_CONTEXT } from 'src/constants';
import { getCustomSearchURL } from 'src/helpers/filters';

/**
 * Valid layout types for different scopes
 */
type LibraryLayout = typeof GRID | typeof INLINE | typeof TWOUP | typeof GALLERY_ONLY;
type GalleryLayout = typeof GRID | typeof INLINE | typeof TWOUP;
type LayoutValue = LibraryLayout | GalleryLayout;

/**
 * View context scope type
 */
type ViewContextScope = typeof VIEW_CONTEXT.LIBRARY | typeof VIEW_CONTEXT.GALLERY;

/**
 * Valid layouts by scope mapping
 */
const VALID_LAYOUTS_BY_SCOPE: Record<ViewContextScope, readonly string[]> = {
  library: [GRID, INLINE, TWOUP, GALLERY_ONLY] as const,
  gallery: [GRID, INLINE, TWOUP] as const,
};

/**
 * Gets the localStorage key for a given scope
 *
 * @param scope - The view context scope
 * @returns The localStorage key
 */
const storageKeyForScope = (scope: ViewContextScope): string =>
  scope === VIEW_CONTEXT.GALLERY ? LOCAL_STORAGE_KEYS.GALLERY_LAYOUT : LOCAL_STORAGE_KEYS.LIBRARY_LAYOUT;

/**
 * Validates if a layout value is valid for a given scope
 *
 * @param scope - The view context scope
 * @param value - The layout value to validate
 * @returns Whether the layout is valid for the scope
 */
export const isValidLayout = (scope: ViewContextScope, value: string): boolean =>
  VALID_LAYOUTS_BY_SCOPE[scope]?.includes(value) ?? false;

/**
 * Persists a layout preference to localStorage
 *
 * @param scope - The view context scope
 * @param layout - The layout value to persist
 */
export const persistLayout = (scope: ViewContextScope, layout: string): void => {
  if (!isValidLayout(scope, layout)) return;
  try {
    window.localStorage.setItem(storageKeyForScope(scope), layout);
  } catch {
    // localStorage not available (private mode or blocked) or access denied; ignore persistence
  }
};

/**
 * Gets the preferred layout for a scope. Explicit URL state wins for the
 * current render, but only user interactions should persist preferences.
 *
 * @param scope - The view context scope (defaults to LIBRARY)
 * @returns The layout value
 */
export const getPersistedLayout = (scope: ViewContextScope = VIEW_CONTEXT.LIBRARY): LayoutValue => {
  // 1) Explicit URL state wins over localStorage.
  const search = getCustomSearchURL();
  const urlLayout = search.get(LAYOUT);
  if (urlLayout && isValidLayout(scope, urlLayout)) {
    return urlLayout as LayoutValue;
  }

  // 2) Fall back to scope-specific localStorage.
  try {
    const stored = window.localStorage.getItem(storageKeyForScope(scope));
    if (stored && isValidLayout(scope, stored)) return stored as LayoutValue;
  } catch {
    // localStorage not available or blocked; fall back to default
  }

  // 3) Default
  return GRID;
};

/**
 * Clear ALL persisted layout keys (library + gallery)
 * Used for full reset (e.g., Akomi logo click)
 */
export const clearAllPersistedLayouts = (): void => {
  try {
    window.localStorage.removeItem(LOCAL_STORAGE_KEYS.LIBRARY_LAYOUT);
    window.localStorage.removeItem(LOCAL_STORAGE_KEYS.GALLERY_LAYOUT);
  } catch {
    // localStorage not available; ignore
  }
};
